import { notify, useModal } from '@billy/design-system'
import { Global } from '@emotion/core'
import { Dialog } from '@shinetools/lumen-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useCreateContact } from '@features/contacts/hooks/useCreateContact'
import type { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ManageSupplier } from '@views/suppliers/routes/SupplierDetail/elements/HeaderSupplierDetail/elements/SupplierDrawer/elements/ManageSupplier'
import type { SupplierFormValues } from '@views/suppliers/routes/SupplierDetail/elements/HeaderSupplierDetail/elements/SupplierDrawer/types/supplierForm'
import { getCreateSupplierContactPayload } from '@views/suppliers/routes/SupplierDetail/elements/HeaderSupplierDetail/elements/SupplierDrawer/utils/getCreateSupplierContactPayload'
import {
  getSupplierFormDefaultValues,
  getSupplierFormSchema,
} from '@views/suppliers/routes/SupplierDetail/elements/HeaderSupplierDetail/elements/SupplierDrawer/utils/supplierForm'

import { ModalId } from '../../../../enums/modalId'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useForm } from '../../../../hooks/useForm'
import { useSegment } from '../../../../hooks/useSegment'
import type { APIResponse } from '../../../../utils'
import * as Styled from './styles'

interface CreateSupplierModalProps {
  defaultValues?: Partial<Contact>
  onContactCreate?: (contact: Contact) => void
}

export const CreateSupplierModal = ({ defaultValues, onContactCreate }: CreateSupplierModalProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { close, isOpen } = useModal(ModalId.CreateContactModal)
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const contact = defaultValues ?? null

  const handleCreateSuccess = useCallback(
    async (data: APIResponse<Contact>) => {
      const createdContact = data.contacts[0]

      await queryClient.invalidateQueries(QueryKeys.Contacts)
      await queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)

      notify({
        id: NotificationKeys.CreateContact,
        message: t('suppliers.detail.form.create.notification.success'),
        variant: 'success',
      })
      close()
      track('Contact Created (Client)')

      if (createdContact) {
        onContactCreate?.(createdContact)
      }
    },
    [close, onContactCreate, queryClient, t, track],
  )

  const handleCreateError = useCallback(() => {
    notify({
      id: NotificationKeys.CreateContact,
      message: t('suppliers.detail.form.create.notification.error'),
      variant: 'error',
    })
  }, [t])

  const { create: createSupplierContact, isProcessing } = useCreateContact({
    onError: handleCreateError,
    onSuccess: handleCreateSuccess,
  })

  const { Form, reset, submitHandler } = useForm({
    defaultValues: useMemo(() => getSupplierFormDefaultValues(contact), [contact]),
    onSubmit: (values: SupplierFormValues) => {
      if (!organization?.id) {
        return
      }

      createSupplierContact(getCreateSupplierContactPayload({ organizationId: organization.id, values }))
    },
    validationSchema: useMemo(() => getSupplierFormSchema(t), [t]),
  })

  // The modal stays mounted in the purchase form, so reopen it from the values passed in this time.
  useEffect(() => {
    if (isOpen) {
      reset(getSupplierFormDefaultValues(contact))
    }
  }, [contact, isOpen, reset])

  const handleClose = useCallback(() => {
    reset(getSupplierFormDefaultValues(contact))
    close()
  }, [close, contact, reset])

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Global styles={Styled.lumenDialogStackingFix} />
      <Dialog
        body={
          <Styled.CreateSupplierModalScrollArea>
            <Form>
              <ManageSupplier contact={contact} trackingContext={TrackingContext.CreateContact} />
            </Form>
          </Styled.CreateSupplierModalScrollArea>
        }
        open={isOpen}
        onClose={handleClose}
        primaryAction={{
          disabled: !organization?.id,
          label: t('suppliers.detail.create_supplier'),
          loading: isProcessing,
          onClick: submitHandler,
        }}
        secondaryAction={{
          label: t('cancel'),
          onClick: handleClose,
        }}
        size="large"
        title={t('suppliers.detail.create_supplier')}
      />
    </>
  )
}
